import { defaultsDeepPreserveArrays } from "@/helpers/utility/CollectionFunctions";

import type { StateTree } from "pinia";
import type { StoreItem } from "./StoreItem";
import type { Subset } from "@/helpers/utility/Type";

export interface ItemStoreState<I extends StoreItem = StoreItem>
  extends StateTree {
  items: { [itemId: number]: I };
  order: number[];
  count: number;
  type: "objectstore";
  itemStructure: I;
}

export interface ItemStoreFunctions<T extends StoreItem = StoreItem> {
  create(itemData: Subset<T>, extraData?: object): Promise<T> | T;
}

export function fillDefaultValues<T extends StoreItem = StoreItem>(
  item: object,
  itemStructure: T
) {
  const newItem = { ...itemStructure };
  for (const key in itemStructure) {
    if (key in item) {
      const k = key as keyof typeof item & keyof T;
      const valueType = typeof item[k];
      if (valueType === "object") {
        newItem[key] = defaultsDeepPreserveArrays(item[k], itemStructure[key]);
      } else {
        newItem[key] = item[k];
      }
    }
  }
  return newItem;
}

export function createStoreItem<T extends StoreItem = StoreItem>(
  store: ItemStoreState<T>,
  itemData: Subset<T>
) {
  const newItem = { ...store.itemStructure, ...itemData };
  newItem.id = store.count++;
  newItem.uid = `${newItem.type}_${newItem.id}`;
  store.items[newItem.id] = newItem;
  store.order.push(newItem.id);
  return newItem;
}

export function deleteStoreItem<T extends StoreItem = StoreItem>(
  store: ItemStoreState<T>,
  item: T
) {
  delete store.items[item.id];
  store.order.splice(store.order.indexOf(item.id), 1);
}

/**
 * Helper method to provide correct typing for store getters,
 * even if typescript doesn't provide the correct type.
 * Use only for getters that DO NOT return a function.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function unwrapGetter<G, S>(getter: G | ((s: S) => G), state: S | any) {
  if (!(getter instanceof Function)) {
    return getter;
  }
  return getter(state);
}

export function getStoreSortFunction<T extends StoreItem>(
  store: Pick<ItemStoreState<T>, "order">
) {
  return (a: T, b: T) => {
    return store.order.indexOf(a.id) - store.order.indexOf(b.id);
  };
}
